import React, { useState } from "react";
import { motion } from "framer-motion";
import { KeyRound, Copy, Check, AlertTriangle } from "lucide-react";

// Shows a refresh token exactly once (on create/rotate) with a copy-to-clipboard button — no
// precedent for this pattern exists elsewhere in the app, so it's built fresh here. The raw
// token is never retrievable again after this modal closes, matching the backend's own
// "shown once" behavior (only a hash is stored server-side).
const RevealTokenModal = ({ label, officeCode, refreshToken, expiresAt, onClose }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(refreshToken);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API unavailable (non-secure context, older browser) — the value is still
            // selectable/visible in the input below, so the user can copy it manually.
        }
    };

    return (
        <motion.div
            className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center px-fluid-3 py-fluid-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <motion.div
                className="bg-white rounded-2xl shadow-xl w-full max-w-[560px] p-fluid-4 space-y-fluid-3"
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
            >
                <div className="flex flex-col items-center space-y-fluid-2 border-b pb-fluid-3">
                    <div className="bg-[#1e2a74] rounded-full p-fluid-2 text-white">
                        <KeyRound size={28} />
                    </div>
                    <h2 className="text-fluid-xl font-semibold text-[#111827]">Refresh Token</h2>
                    <p className="text-fluid-base text-gray-600 text-center">
                        For <span className="font-semibold">{label}</span> ({officeCode})
                    </p>
                </div>

                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-fluid-3 py-fluid-2">
                    <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-fluid-sm text-amber-800">
                        This is shown only once. Copy it now and store it in the integration's own
                        config (never in a repo) — it will not be shown again.
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <input
                        readOnly
                        value={refreshToken}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 font-mono text-fluid-sm border border-gray-300 rounded-xl px-3 py-2 bg-gray-50 text-gray-900 truncate"
                    />
                    <button
                        onClick={handleCopy}
                        className={`shrink-0 flex items-center gap-1.5 px-fluid-button-x py-fluid-button-y rounded text-fluid-sm font-semibold transition ${
                            copied
                                ? "bg-green-600 text-white"
                                : "bg-[#1e2a74] hover:bg-[#18225c] text-white"
                        }`}
                    >
                        {copied ? <Check size={16} /> : <Copy size={16} />}
                        {copied ? "Copied!" : "Copy"}
                    </button>
                </div>

                {expiresAt && (
                    <p className="text-fluid-sm text-gray-500">
                        Expires: {new Date(expiresAt).toLocaleString()}
                    </p>
                )}

                <div className="flex justify-end pt-fluid-2 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-fluid-button-x py-fluid-button-y bg-[#1e2a74] hover:bg-[#18225c] text-white text-fluid-sm rounded font-semibold"
                    >
                        Done
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default RevealTokenModal;
